import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import {
    ArrowDownToLine,
    CheckCircle2,
    Clock,
    Copy,
    ExternalLink,
    RadioTower,
    RefreshCw,
    Search,
    Send,
    Wallet,
} from "lucide-react";

function usd(n) {
    return `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function shortHash(h) {
    if (!h) return "—";
    return h.length > 20 ? `${h.slice(0, 10)}…${h.slice(-6)}` : h;
}

function relTime(iso) {
    if (!iso) return "—";
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return `${s}s önce`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}dk önce`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}sa önce`;
    return `${Math.floor(h / 24)}g önce`;
}

const VIA_LABEL = {
    order: "Sipariş eşleşti",
    auto: "Otomatik (siparişsiz)",
    force: "Manuel kurtarma",
    legacy: "Önceden yüklenmiş",
};

function SummaryCard({ Icon, label, value, sub, accent, testId }) {
    return (
        <div data-testid={testId} className="bg-card border border-white/[0.07] rounded-2xl p-4 md:p-5">
            <div className="flex items-center gap-2 mb-2">
                <div
                    className="w-8 h-8 rounded-lg grid place-items-center"
                    style={{ background: `${accent}18`, color: accent, border: `1px solid ${accent}30` }}
                >
                    <Icon className="w-4 h-4" />
                </div>
                <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
            </div>
            <p className="font-display font-black text-2xl md:text-3xl tabular-nums">{value}</p>
            {sub && <p className="text-[11px] text-muted-foreground font-mono mt-0.5">{sub}</p>}
        </div>
    );
}

function StatusBadge({ d }) {
    if (d.status === "credited") {
        return (
            <span data-testid={`admin-deposit-status-${d.id}`} className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                <CheckCircle2 className="w-3 h-3" /> Yüklendi
            </span>
        );
    }
    return (
        <span data-testid={`admin-deposit-status-${d.id}`} className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-yellow-500/15 text-yellow-300 border border-yellow-500/30">
            <Clock className="w-3 h-3" /> Onay {d.confirmations}/{d.confirmations_required}
        </span>
    );
}

function DepositRow({ d }) {
    const copy = (t) => {
        navigator.clipboard?.writeText(t).then(() => toast.success("Kopyalandı")).catch(() => toast.error("Kopyalama engellendi"));
    };
    return (
        <div data-testid={`admin-deposit-row-${d.id}`} className="px-4 md:px-5 py-4 grid grid-cols-1 md:grid-cols-[1.4fr_1fr_1fr_auto] gap-3 md:gap-4 md:items-center hover:bg-white/[0.02] transition-colors">
            <div className="min-w-0">
                <p className="font-medium text-sm truncate" data-testid={`admin-deposit-user-${d.id}`}>{d.user_email || d.user_id}</p>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <StatusBadge d={d} />
                    {d.credited_via && (
                        <span className="text-[10px] font-mono text-muted-foreground">{VIA_LABEL[d.credited_via] || d.credited_via}</span>
                    )}
                </div>
                {d.admin_note && <p className="text-[11px] text-yellow-300/90 mt-1">{d.admin_note}</p>}
            </div>
            <div>
                <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{d.network} · {d.currency.replace("_", " ")}</p>
                <p className="font-display font-semibold text-base tabular-nums">
                    {Number(d.amount_crypto).toFixed(2)} <span className="text-muted-foreground text-xs">→</span>{" "}
                    <span className={d.status === "credited" ? "text-emerald-400" : "text-yellow-300"}>{usd(d.amount_usd)}</span>
                </p>
            </div>
            <div className="min-w-0">
                <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">TxID</p>
                <div className="flex items-center gap-1.5">
                    <code className="text-[12px] font-mono truncate">{shortHash(d.tx_hash)}</code>
                    <button onClick={() => copy(d.tx_hash)} className="text-muted-foreground hover:text-foreground" aria-label="Copy tx" data-testid={`admin-deposit-copy-${d.id}`}>
                        <Copy className="w-3.5 h-3.5" />
                    </button>
                    {d.explorer_url && (
                        <a href={d.explorer_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80" aria-label="Explorer" data-testid={`admin-deposit-explorer-${d.id}`}>
                            <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                    )}
                </div>
                <p className="text-[10px] font-mono text-muted-foreground truncate mt-0.5">{d.address}</p>
            </div>
            <div className="md:text-right">
                <p className="text-[11px] font-mono text-muted-foreground">
                    {d.status === "credited" ? "Yüklendi" : "Görüldü"} · {relTime(d.credited_at || d.first_seen_at)}
                </p>
                <p className="text-[10px] font-mono text-muted-foreground/70">{new Date(d.credited_at || d.first_seen_at).toLocaleString()}</p>
            </div>
        </div>
    );
}

export default function AdminDeposits() {
    const [data, setData] = useState(null);
    const [watcher, setWatcher] = useState(null);
    const [status, setStatus] = useState("");
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(false);
    const [job, setJob] = useState(null);
    const jobTimer = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (status) params.status = status;
            if (q.trim()) params.q = q.trim();
            const [d, w] = await Promise.allSettled([
                api.get("/admin/deposits", { params }),
                api.get("/admin/deposits/watcher-status"),
            ]);
            if (d.status === "fulfilled") setData(d.value.data);
            if (w.status === "fulfilled") setWatcher(w.value.data);
        } finally {
            setLoading(false);
        }
    }, [status, q]);

    useEffect(() => {
        load();
        const id = setInterval(load, 20000);
        return () => clearInterval(id);
    }, [load]);

    useEffect(() => () => jobTimer.current && clearInterval(jobTimer.current), []);

    const scanNow = async () => {
        try {
            const { data: res } = await api.post("/admin/deposits/reconcile-now");
            setJob({ id: res.job_id, status: "queued", users_total: res.users_queued, users_done: 0, new_topup_transactions: 0 });
            toast.success(`${res.users_queued} kullanıcı adresi taranıyor…`);
            if (jobTimer.current) clearInterval(jobTimer.current);
            jobTimer.current = setInterval(async () => {
                try {
                    const { data: j } = await api.get(`/admin/deposits/reconcile-status/${res.job_id}`);
                    setJob(j);
                    if (j.status === "completed" || j.status === "failed") {
                        clearInterval(jobTimer.current);
                        jobTimer.current = null;
                        load();
                        if (j.status === "completed") {
                            toast.success(`Tarama bitti · ${j.new_topup_transactions} yeni yükleme`);
                        } else {
                            toast.error(`Tarama hatası: ${j.error || "bilinmiyor"}`);
                        }
                    }
                } catch {
                    /* ignore */
                }
            }, 2500);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Tarama başlatılamadı");
        }
    };

    const s = data?.summary || {};
    const items = data?.items || [];
    const watcherHealthy = Boolean(watcher?.last_cycle_finished_at) && !watcher?.last_error;

    return (
        <div data-testid="admin-deposits" className="space-y-6 md:space-y-8">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <h1 className="font-display font-bold text-2xl md:text-4xl tracking-tight">Gelen Ödemeler</h1>
                    <p className="text-muted-foreground mt-1 text-sm">
                        Zincirde görülen her transfer burada anında listelenir: kim, hangi ağ, kaç USDT, TxID ve bakiyeye yüklendi mi.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={load} data-testid="admin-deposits-refresh" className="h-10 border-white/10 bg-white/[0.02] hover:bg-white/[0.06]">
                        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Yenile
                    </Button>
                    <Button onClick={scanNow} disabled={job && job.status !== "completed" && job.status !== "failed"} data-testid="admin-deposits-scan-now" className="h-10 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold">
                        <Search className="w-4 h-4 mr-2" /> Şimdi tara
                    </Button>
                </div>
            </div>

            {/* Watcher status */}
            <div data-testid="admin-deposits-watcher" className={`rounded-2xl border px-4 py-3 text-sm flex flex-col md:flex-row md:items-center gap-2 md:gap-6 ${watcherHealthy ? "border-emerald-500/25 bg-emerald-500/[0.05]" : "border-yellow-500/25 bg-yellow-500/[0.05]"}`}>
                <span className="inline-flex items-center gap-2 font-medium">
                    <RadioTower className={`w-4 h-4 ${watcherHealthy ? "text-emerald-400" : "text-yellow-300"}`} />
                    Otomatik takip {watcherHealthy ? "çalışıyor" : watcher?.last_error ? "hata verdi" : "başlatılıyor…"}
                </span>
                <span className="text-muted-foreground font-mono text-xs">
                    Son tur: {watcher?.last_cycle_finished_at ? relTime(watcher.last_cycle_finished_at) : "—"} · {watcher?.last_cycle_users ?? 0} adres
                </span>
                <span className="text-muted-foreground font-mono text-xs">
                    Toplam {watcher?.total_users ?? 0} kullanıcı · tam tur ≈ {watcher?.full_rotation_secs_estimate ? `${Math.ceil(watcher.full_rotation_secs_estimate / 60)} dk` : "—"}
                </span>
                <span className="text-muted-foreground font-mono text-xs inline-flex items-center gap-1">
                    <Send className="w-3 h-3" /> Telegram {watcher?.telegram_configured ? "aktif" : "kapalı"}
                </span>
                {watcher?.last_error && <span className="text-yellow-300 text-xs font-mono truncate">{watcher.last_error}</span>}
                {job && (
                    <span data-testid="admin-deposits-job" className="text-xs font-mono text-primary md:ml-auto">
                        Tarama: {job.status} · {job.users_done}/{job.users_total} · yeni: {job.new_topup_transactions}
                    </span>
                )}
            </div>

            {/* Summary */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                <SummaryCard Icon={ArrowDownToLine} label="Bugün" value={usd(s.today_usd)} sub={`${s.today_count || 0} yükleme`} accent="#10B981" testId="deposits-stat-today" />
                <SummaryCard Icon={Wallet} label="Bu ay" value={usd(s.month_usd)} sub={`${s.month_count || 0} yükleme`} accent="#22D3EE" testId="deposits-stat-month" />
                <SummaryCard Icon={Clock} label="Onay bekleyen" value={usd(s.confirming_usd)} sub={`${s.confirming_count || 0} transfer`} accent="#FBBF24" testId="deposits-stat-confirming" />
                <SummaryCard Icon={CheckCircle2} label="Toplam yüklenen" value={usd(s.total_usd)} sub={`${s.total_count || 0} yükleme`} accent="#2FA57C" testId="deposits-stat-total" />
            </div>

            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-2 md:items-center">
                <div className="flex gap-1 bg-background/60 border border-white/10 rounded-lg p-0.5 w-max">
                    {[["", "Tümü"], ["confirming", "Onay bekliyor"], ["credited", "Yüklendi"]].map(([v, label]) => (
                        <button
                            key={v || "all"}
                            onClick={() => setStatus(v)}
                            data-testid={`deposits-filter-${v || "all"}`}
                            className={`px-3 h-9 text-xs font-medium rounded-md transition-colors ${status === v ? "bg-white/10 text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="E-posta, TxID veya adres ara…"
                        data-testid="deposits-search"
                        className="bg-background/60 border-white/10 h-10 pl-8"
                    />
                </div>
            </div>

            {/* List */}
            <div className="bg-card border border-white/[0.07] rounded-2xl overflow-hidden">
                <div className="hidden md:grid px-5 py-2.5 grid-cols-[1.4fr_1fr_1fr_auto] gap-4 text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground border-b border-white/[0.06]">
                    <span>Kullanıcı · Durum</span>
                    <span>Ağ · Tutar</span>
                    <span>TxID · Adres</span>
                    <span className="text-right">Zaman</span>
                </div>
                {items.length === 0 ? (
                    <div className="px-5 py-14 text-center text-sm text-muted-foreground" data-testid="admin-deposits-empty">
                        {loading ? "Yükleniyor…" : "Henüz zincirde görülen bir ödeme yok. Bir kullanıcı adresine transfer geldiğinde burada görünür."}
                    </div>
                ) : (
                    <div className="divide-y divide-white/[0.05]">
                        {items.map((d) => <DepositRow key={d.id} d={d} />)}
                    </div>
                )}
            </div>
        </div>
    );
}
